/**
 * Configuration for material-picking ERP integration fields.
 *
 * These keys mirror the dynamics365-connector defaults (see its
 * D365_*_CUSTOM_FIELD_KEY env vars in src/config/configuration.ts). Pick
 * consumptions are written to the work order operation's customFields[PL_CONSUMPTIONS_KEY]
 * as an array of { [LP_NUMBER_KEY], [CONSUMPTION_QTY_KEY] } entries. The connector
 * reads pending entries (those without PL_JOURNAL_ID_KEY), posts the
 * ProductionPickingListJournalEntries to D365, then stamps PL_JOURNAL_ID_KEY back
 * onto each entry for idempotency.
 *
 * NOTE: Configuration is currently hardcoded for initial release, matching the
 * getBarcodeFieldConfig precedent.
 * Future enhancement: Move to company/machine settings to allow per-customer
 * configuration.
 */

// Array field key on the work order operation holding pick consumptions.
export const PL_CONSUMPTIONS_KEY = 'plConsumptions';

// Map field key on the work order operation holding the available materials
// written by the connector's available-materials sync, keyed by item number:
// { [itemNumber]: { requiredQty, qtyPerUnit, unit, lps: [{ lp, onHand, warehouse, location?, batch? }] } }
export const AVAILABLE_MATERIALS_KEY = 'availableMaterials';

// Per-entry sub-keys.
export const LP_NUMBER_KEY = 'licensePlateNumber';
export const CONSUMPTION_QTY_KEY = 'consumptionQty';

// Optional per-entry sub-keys written by OperatorView (the connector ignores
// them). They make entries self-describing so pick rows can be rebuilt from
// the durable record after a reload: itemNumber groups the row under its
// material, pickId dedupes against in-memory Redux picks, location is display.
export const ITEM_NUMBER_KEY = 'itemNumber';
export const PICK_ID_KEY = 'pickId';
export const LOCATION_KEY = 'location';

// Written back by the connector after a successful D365 post; its presence marks
// an entry as already submitted (idempotency). OperatorView must never set it.
export const PL_JOURNAL_ID_KEY = 'plJournalId';

// Set by the connector on picks it imports from D365 (picked or returned there
// rather than in OperatorView). Such entries are already posted.
export const SOURCE_KEY = 'source';
export const D365_SOURCE = 'd365';

// Written by the connector on the WOO. reportedOutsideMM is the number of parts
// reported as finished on the order in D365 by journals MM didn't make
// (handhelds, supervisors). rafResults is keyed by labor ticket ref; an entry's
// pickShortfall ({ itemNumber, shortQty, unit, at }) means the connector is
// holding that ticket because D365 would reject it for short picks.
export const REPORTED_OUTSIDE_MM_KEY = 'reportedOutsideMM';
export const RAF_RESULTS_KEY = 'rafResults';

// The triage app's ledger on the WOO (mm-integrations triage CONNECTOR_CONTRACT.md).
export const TRIAGE_KEY = 'triage';

// Picking is scan-only: an LP is added by scanning its barcode. Development and
// staging builds also have a hidden manual mode (press and hold the pick step's
// title) in which an LP can be selected by tapping its tile, so the wizard can be
// exercised without a barcode scanner. Production stays scan-only.
export { isDevOrStaging as MANUAL_PICKING_MODE_AVAILABLE } from 'lib/appConfig';
